import React, { useState } from 'react';
import { useAuth } from '../context/AuthProvider';
import { useChat } from '../context/ChatProvider';
import { ChatWindow } from '../components/ChatWindow';
import { InboxPanel } from '../components/InboxPanel';
import { ActiveConversationsPanel } from '../components/ActiveConversationsPanel';
import { FaSignOutAlt, FaInbox, FaBars } from 'react-icons/fa';

// ===================================================================================
// A. Redesigned, Reusable Sidebar Content
// ===================================================================================
function AgentSidebarContent({ user, logout, requests }) {
    return (
        <>
            {/* User Profile Section */}
            <div className="p-4 h-24 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-12 h-12 bg-cyan-600 rounded-full text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                        {user?.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="overflow-hidden">
                        <h2 className="font-bold text-white truncate">{user?.name}</h2>
                        <p className="text-xs text-green-400 font-semibold">Online</p>
                    </div>
                </div>
                <button 
                    onClick={logout} 
                    className="p-3 rounded-full text-slate-400 hover:bg-red-500/20 hover:text-red-400 transition-colors flex-shrink-0" 
                    title="Log Out">
                    <FaSignOutAlt size={18}/>
                </button>
            </div>
            
            {/* Inbox Header */}
            <div className="p-4 border-b border-slate-800">
                <div className="w-full flex items-center gap-3 text-white font-semibold">
                    <FaInbox />
                    <span>Inbox</span>
                    {requests.length > 0 && (
                        <span className="ml-auto text-xs font-bold text-white bg-red-600 rounded-full h-5 w-5 flex items-center justify-center">
                            {requests.length}
                        </span>
                    )}
                </div>
            </div>

            {/* Inbox Panel Container */}
            <div className="flex-1 overflow-y-auto">
                {/* The InboxPanel component itself will need to be restyled to match the dark theme */}
                <InboxPanel />
            </div>
        </>
    );
}

// ===================================================================================
// B. Main Agent Dashboard Page
// ===================================================================================
export function DashboardPage() {
    const { user, logout } = useAuth();
    const { requests } = useChat();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <div className="flex h-screen bg-gray-900 font-sans text-gray-300">

            {/* --- MOBILE OVERLAY SIDEBAR --- */}
            <div
                className={`fixed inset-0 bg-black/60 z-30 transition-opacity lg:hidden ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsSidebarOpen(false)}
            />
            <aside className={`fixed inset-y-0 left-0 z-40 w-80 bg-slate-900 border-r border-slate-800 flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <AgentSidebarContent user={user} logout={logout} requests={requests} />
            </aside>

            {/* --- DESKTOP SIDEBAR (Static) --- */}
            <aside className="w-80 flex-shrink-0 bg-slate-900 border-r border-slate-800 hidden lg:flex flex-col">
                <AgentSidebarContent user={user} logout={logout} requests={requests} />
            </aside>

            {/* --- MAIN CONTENT AREA --- */}
            <main className="flex-1 flex flex-col overflow-hidden">
                {/* Top Header Bar for Mobile */}
                <header className="h-24 p-4 border-b border-gray-800 flex items-center lg:hidden">
                    <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md text-gray-400 hover:bg-gray-800" aria-label="Open Menu">
                        <FaBars size={24}/>
                    </button>
                </header>

                {/* This wrapper ensures the correct flex layout for all screen sizes */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Active Conversations Panel Container */}
                    <div className="bg-gray-800/50 border-b border-gray-700">
                        {/* The ActiveConversationsPanel component will also need to be restyled */}
                        <ActiveConversationsPanel />
                    </div>

                    {/* Chat Window Container */}
                    <div className="flex-1 overflow-hidden">
                        {/* The ChatWindow component is the final piece to be restyled */}
                        <ChatWindow />
                    </div>
                </div>
            </main>
        </div>
    );
}